import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Loader2 } from 'lucide-react';
import { User, BillingProfile } from '../../types';
import { api } from '../../services/api';

interface BillingProfileFormProps {
    user: User;
    currentProfile?: BillingProfile;
    onSave: () => void;
}

export const BillingProfileForm: React.FC<BillingProfileFormProps> = ({ user, currentProfile, onSave }) => {
    const [formData, setFormData] = useState<Partial<BillingProfile>>(currentProfile || {
        type: 'individual',
        name: user.config.companyName || '',
        email: user.config.contactEmail || user.email,
        country: 'GR', // Default
        address: '',
        city: '',
        postalCode: '',
        isDefault: true
    });
    const [loading, setLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const { t } = useTranslation('billing');

    useEffect(() => {
        if (currentProfile) {
            setFormData(currentProfile);
        }
    }, [currentProfile]);

    const handleChange = (key: keyof BillingProfile, value: any) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleValidateVat = async () => {
        if (!formData.vatNumber || formData.vatNumber.length < 5) {
            alert(t('profile.error_vat_invalid'));
            return;
        }

        setIsValidating(true);
        try {
            const result = await api.validateVat(formData.vatNumber);
            if (result.valid) {
                setFormData(prev => ({
                    ...prev,
                    vatValid: true,
                    name: result.name || prev.name, // Auto-fill
                    address: result.address || prev.address // Auto-fill
                }));
                alert(t('profile.vat_valid_msg', { name: result.name, address: result.address }));
            } else {
                setFormData(prev => ({ ...prev, vatValid: false }));
                alert(t('profile.vat_not_found'));
            }
        } catch (e: any) {
            alert(t('profile.error_vat_api') + e.message);
        } finally {
            setIsValidating(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await api.saveBillingProfile(formData as any);
            setSuccess(t('profile.success_save'));
            onSave();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mt-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <CreditCard size={20} /> {t('profile.title')}
            </h3>

            {error && <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}
            {success && <div className="mb-4 bg-green-50 text-green-600 p-3 rounded-lg text-sm">{success}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="type" checked={formData.type === 'individual'} onChange={() => handleChange('type', 'individual')} className="accent-black" />
                        <span className="text-sm font-medium">{t('profile.individual')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="type" checked={formData.type === 'company'} onChange={() => handleChange('type', 'company')} className="accent-black" />
                        <span className="text-sm font-medium">{t('profile.company')}</span>
                    </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.name_label')}</label>
                        <input type="text" required value={formData.name} onChange={e => handleChange('name', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.email_label')}</label>
                        <input type="email" required value={formData.email} onChange={e => handleChange('email', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black" />
                    </div>
                </div>

                {formData.type === 'company' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                        <div className="md:col-span-1">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.country')}</label>
                            <select value={formData.country} onChange={e => handleChange('country', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none bg-white">
                                <option value="GR">Greece (GR)</option>
                                <option value="US">United States (US)</option>
                                <option value="GB">United Kingdom (GB)</option>
                                <option value="DE">Germany (DE)</option>
                                <option value="FR">France (FR)</option>
                                <option value="IT">Italy (IT)</option>
                                <option value="ES">Spain (ES)</option>
                                <option value="NL">Netherlands (NL)</option>
                                <option value="BE">Belgium (BE)</option>
                                <option value="CY">Cyprus (CY)</option>
                                {/* Add more as needed */}
                            </select>
                        </div>
                        <div className="md:col-span-1">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.vat_number')}</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    required
                                    value={formData.vatNumber || ''}
                                    onChange={e => handleChange('vatNumber', e.target.value)}
                                    className={`w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black ${formData.vatValid ? 'border-green-500 bg-green-50' : 'border-slate-300'}`}
                                    placeholder={t('profile.vat_placeholder')}
                                />
                                <button
                                    type="button"
                                    onClick={handleValidateVat}
                                    disabled={isValidating || !formData.vatNumber}
                                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-200 transition whitespace-nowrap"
                                >
                                    {isValidating ? <Loader2 className="animate-spin" size={14} /> : t('profile.validate_btn')}
                                </button>
                            </div>
                        </div>
                        <div className="md:col-span-1">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.tax_office')}</label>
                            <input type="text" value={formData.taxOffice || ''} onChange={e => handleChange('taxOffice', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black" />
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.address')}</label>
                        <input type="text" required value={formData.address} onChange={e => handleChange('address', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.city')}</label>
                        <input type="text" required value={formData.city} onChange={e => handleChange('city', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.postal_code')}</label>
                        <input type="text" required value={formData.postalCode} onChange={e => handleChange('postalCode', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black" />
                    </div>
                    {formData.type === 'individual' && (
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('profile.region')}</label>
                            <select value={formData.country} onChange={e => handleChange('country', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none bg-white">
                                <option value="EU">{t('profile.within_eu')}</option>
                                <option value="NON_EU">{t('profile.outside_eu')}</option>
                            </select>
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-4">
                    <button type="submit" disabled={loading} className="bg-black text-white px-6 py-2 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2">
                        {loading && <Loader2 className="animate-spin" size={16} />}
                        {loading ? t('profile.saving') : t('profile.save_btn')}
                    </button>
                </div>
            </form>
        </div>
    );
};
